import type { PhotoTag, PhotoType } from "./api";
import { callTime } from "./calls";

export const MAX_SIDE = 1600;
export const MAX_PHOTO_BYTES = 5_000_000;
export const JPEG_QUALITY = 0.8;
export const PHOTO_TYPES: readonly PhotoType[] = ["image/jpeg", "image/png", "image/webp"];
export const TAGS: readonly PhotoTag[] = ["stop_proof", "condition", "access"];
export const TAG_TEXT: Record<PhotoTag, string> = {
  stop_proof: "Stop proof",
  condition: "Container condition",
  access: "Access",
};

/** The size that keeps the shape and makes the longest side at most `most` pixels. */
export function targetSize(width: number, height: number, most = MAX_SIDE): { width: number; height: number } {
  if (width <= 0 || height <= 0) return { width: 0, height: 0 };
  const scale = Math.min(1, most / Math.max(width, height));
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

/** Why the browser cannot send this file as it is, or null. */
export function photoProblem(type: string, size: number): string | null {
  if (!(PHOTO_TYPES as readonly string[]).includes(type)) return "Only a JPEG, PNG or WebP photo is accepted.";
  if (size < 1) return "The photo is empty.";
  if (size > MAX_PHOTO_BYTES) return `A photo can have ${MAX_PHOTO_BYTES / 1_000_000} MB at most.`;
  return null;
}

/** The time of a photo: the file time, or now when the file time is not plausible. */
export function takenAt(fileTime: number, now: number): string {
  const month = 30 * 24 * 3600 * 1000;
  const plausible = fileTime > now - month && fileTime <= now + 60_000;
  return new Date(plausible ? fileTime : now).toISOString();
}

/** A work-order note that names a condition photo. */
export function orderNote(taken: string, timeZone: string): string {
  return `Container condition photo of ${callTime(taken, timeZone)}.`;
}

/** Make the photo smaller in the browser: JPEG, longest side MAX_SIDE. A file it cannot read stays as it is. */
export async function shrink(file: Blob): Promise<Blob> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file;
  }
  const size = targetSize(bitmap.width, bitmap.height);
  const canvas = document.createElement("canvas");
  canvas.width = size.width;
  canvas.height = size.height;
  canvas.getContext("2d")?.drawImage(bitmap, 0, 0, size.width, size.height);
  bitmap.close();
  const made = await new Promise<Blob | null>((done) => canvas.toBlob(done, "image/jpeg", JPEG_QUALITY));
  return made && made.size < file.size ? made : file;
}

/** The place of the phone, when the browser gives it in 4 seconds. A prompt with no answer gives null. */
export function position(): Promise<{ lat: number; lon: number } | null> {
  if (!("geolocation" in navigator)) return Promise.resolve(null);
  return new Promise((done) => {
    const late = setTimeout(() => done(null), 4000);
    navigator.geolocation.getCurrentPosition(
      (found) => {
        clearTimeout(late);
        done({ lat: found.coords.latitude, lon: found.coords.longitude });
      },
      () => {
        clearTimeout(late);
        done(null);
      },
      { timeout: 3000, maximumAge: 60_000 },
    );
  });
}
